import dayjs from 'dayjs';
import ReactDOM from 'react-dom';
import weekOfYear from "dayjs/plugin/weekOfYear";
import { Select, DatePicker } from 'design-system';
import { useRef, useState, useEffect } from 'react';
import updateLocale from "dayjs/plugin/updateLocale";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import mockCalendar from 'src/utils/date/mock-calendar.json';
import { getYearAndSequence, formatDateByFrequency } from 'src/utils/frequencyTypeTransform';

import { useConfigStore } from 'src/stores/config';

import { FrequencyType } from 'src/types/common';

import './target-cycle-picker.css';
import WeeklyCalendar from './WeeklyCalendar'; // 引入自定义样式

dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);

// 设置周一为一周的起始日
dayjs.updateLocale('zh-cn', {
  weekStart: 1 // 周一
});

// 清除图标
const CloseIcon = () => (
  <svg viewBox="0 0 1024 1024" width="1em" height="1em" fill="currentColor">
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9c-4.4 5.2-.7 13.1 6.1 13.1h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
);

interface WeekData {
  sequence: number;
  weeklySequence: number;
  startDate: string;
  endDate: string;
}

interface CalendarData {
  [year: string]: WeekData[];
}

export interface TargetCycle {
  type: FrequencyType | undefined;
  date: dayjs.Dayjs | null;
  sequence?: number;
}

interface TargetCycleProps {
  targetCycle?: TargetCycle;
  allowClear?: boolean;
  minDate?: dayjs.Dayjs;
  onChange: (targetCycle: TargetCycle) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  availableTypes?: FrequencyType[];
  disableTypeSelector?: boolean; // 新增：是否禁用周期类型选择器
}

// 获取日历数据的 hook
const useCalendarData = () => {
  const { calendarConfig } = useConfigStore();
  return calendarConfig as CalendarData | null;
};

// 检查日期是否在有效的周范围内
const isDateInValidWeek = (date: dayjs.Dayjs, calendarData: CalendarData | null): boolean => {
  if (!calendarData) return false;
  
  const year = date.year().toString();
  const yearData = calendarData[year];
  
  if (!yearData) return false;
  
  return yearData.some(week => {
    const startDate = dayjs(week.startDate);
    const endDate = dayjs(week.endDate);
    return date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
  });
};

// 获取日期所在的周数据
const getWeekData = (date: dayjs.Dayjs | null, calendarData: CalendarData | null): WeekData | null => {
  if (!date) return null;

  // 确保 date 是 dayjs 对象
  const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);

  // 优先使用全局的 calendarConfig；若未加载则回退到本地 mock-calendar
  const data: CalendarData | null = calendarData ?? (mockCalendar as CalendarData);
  if (!data) return null;

  const year = dayjsDate.year().toString();
  const yearData = data[year];

  if (!yearData) return null;

  return (
    yearData.find((week) => {
      const startDate = dayjs(week.startDate);
      const endDate = dayjs(week.endDate);
      return (
        dayjsDate.isSameOrAfter(startDate, 'day') &&
        dayjsDate.isSameOrBefore(endDate, 'day')
      );
    }) || null
  );
};

export default function TargetCyclePicker({ targetCycle, allowClear, minDate, onChange, disabled, style, availableTypes, disableTypeSelector }: TargetCycleProps) {
  const [selectedPicker, setSelectedPicker] = useState<FrequencyType | undefined>(targetCycle?.type);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(targetCycle?.date ?? null);
  const [showWeeklyCalendar, setShowWeeklyCalendar] = useState(false);
  const calendarData = useCalendarData();
  const datePickerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedPicker(targetCycle?.type);
    setSelectedDate(targetCycle?.date || null);
  }, [targetCycle]);

  // 当 availableTypes 改变时，检查当前选中的类型是否仍然可用
  useEffect(() => {
    if (availableTypes && selectedPicker && !availableTypes.includes(selectedPicker)) {
      setSelectedPicker(undefined);
      setSelectedDate(null);
      onChange({ type: undefined, date: null, sequence: undefined });
    }
  }, [availableTypes, selectedPicker, onChange]);

  const allDateTypeOptions = [
    { label: "年", value: FrequencyType.Yearly },
    { label: "季", value: FrequencyType.Quarterly },
    { label: "月", value: FrequencyType.Monthly },
    { label: "周", value: FrequencyType.Weekly },
  ];

  // 根据 availableTypes 过滤选项
  const dateTypeOptions = availableTypes 
    ? allDateTypeOptions.filter(option => availableTypes.includes(option.value))
    : allDateTypeOptions;

  const getPlaceholder = (type: TargetCycle["type"]) => {
    const option = dateTypeOptions.find((opt) => opt.value === type);
    return option ? `请选择${option.label}` : "请选择";
  };

  const mapFrequencyToPickerMode = (frequency?: FrequencyType): 'date' | 'week' | 'month' | 'quarter' | 'year' => {
    switch (frequency) {
      case FrequencyType.Yearly:
        return 'year';
      case FrequencyType.Quarterly:
        return 'quarter';
      case FrequencyType.Monthly:
        return 'month';
      case FrequencyType.Weekly:
        return 'week';
      default:
        return 'date';
    }
  };

  const formatDate = (date: dayjs.Dayjs | Date | string | null) => {
    if (!date) return '';
    if (!selectedPicker) return '';

    // 确保 date 是 dayjs 对象
    const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);

    if (selectedPicker === FrequencyType.Weekly) {
      const weekData = getWeekData(dayjsDate, calendarData);
      if (weekData) {
        const startDate = dayjs(weekData.startDate);
        const endDate = dayjs(weekData.endDate);
        const month = (startDate.month() + 1).toString().padStart(2, '0');
        // return `${startDate.year()}年${month}月第${weekData.weeklySequence}周 (${startDate.format('MM/DD')}-${endDate.format('MM/DD')})`;
        return `${startDate.year()}年${month}月第${weekData.weeklySequence}周`;
      }
    }

    const [year, sequence] = getYearAndSequence[selectedPicker](dayjsDate);
    return formatDateByFrequency(year, sequence, selectedPicker);
  };

  const handleTypeChange = (value: TargetCycle["type"]) => {
    setSelectedPicker(value);
    setSelectedDate(null);
    setShowWeeklyCalendar(false);
    onChange({ type: value, date: null, sequence: undefined });
  };

  // 处理周选择器的显示
  const handleWeeklyCalendarToggle = () => {
    if (disabled || !calendarData) return; // 如果没有日历数据或禁用状态，则不显示选择器
    
    // 切换日历显示状态
    setShowWeeklyCalendar(!showWeeklyCalendar);
    
    // 如果是显示日历，添加一个小延迟让日历有时间渲染
    if (!showWeeklyCalendar) {
      setTimeout(() => {
        const calendarEl = document.querySelector('.weekly-calendar');
        const inputEl = inputRef.current;
        
        if (calendarEl && inputEl) {
          const inputRect = inputEl.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          
          // 直接设置日历位置
          if (inputRect.bottom + 300 > viewportHeight) {
            // 如果下方空间不足，显示在上方
            calendarEl.setAttribute('style', `
              position: fixed !important; 
              z-index: 10000 !important;
              bottom: ${window.innerHeight - inputRect.top + 2}px !important;
              left: ${inputRect.left}px !important;
            `);
          } else {
            // 显示在下方
            calendarEl.setAttribute('style', `
              position: fixed !important; 
              z-index: 10000 !important;
              top: ${inputRect.bottom + 2}px !important;
              left: ${inputRect.left}px !important;
            `);
          }
        }
      }, 10);
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | Date | string | null) => {
    if (!date || selectedPicker === undefined) {
      setSelectedDate(null);
      onChange({ type: undefined, date: null, sequence: undefined });
      return;
    }

    // 确保 date 是 dayjs 对象
    const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);

    if (selectedPicker === FrequencyType.Weekly) {
      // 当选择周类型时，查找对应的周数据
      const weekData = getWeekData(dayjsDate, calendarData);
      if (weekData) {
        const startDate = dayjs(weekData.startDate);
        setSelectedDate(startDate);
        onChange({ type: selectedPicker, date: startDate, sequence: weekData.sequence });
      } else {
        // 若找不到周数据，直接使用选择的日期
        setSelectedDate(dayjsDate);
        onChange({ type: selectedPicker, date: dayjsDate, sequence: undefined });
      }
      return;
    }

    const updatedDate = dayjsDate.startOf(mapFrequencyToPickerMode(selectedPicker) as dayjs.UnitType);
    setSelectedDate(updatedDate);
    onChange({ type: selectedPicker, date: updatedDate });
  };

  const handleWeeklyCalendarChange = (date: dayjs.Dayjs) => {
    // 当从周历选择器选择日期时，查找对应的周数据
    const weekData = getWeekData(date, calendarData);
    if (weekData) {
      setSelectedDate(date);
      onChange({ type: selectedPicker, date, sequence: weekData.sequence });
    } else {
      setSelectedDate(date);
      onChange({ type: selectedPicker, date, sequence: undefined });
    }
    setShowWeeklyCalendar(false);
  };

  const disabledDate = (currentDate: dayjs.Dayjs | Date | string) => {
    if (!currentDate) return false;

    // 确保 currentDate 是 dayjs 对象
    const dayjsDate = dayjs.isDayjs(currentDate) ? currentDate : dayjs(currentDate);

    // 从calendarConfig获取可用年份范围
    if (calendarData) {
      const availableYears = Object.keys(calendarData).map(year => parseInt(year, 10));
      
      if (availableYears.length > 0) {
        const minYear = Math.min(...availableYears);
        const maxYear = Math.max(...availableYears);
        
        // 年份选择器
        if (selectedPicker === FrequencyType.Yearly) {
          return dayjsDate.year() < minYear || dayjsDate.year() > maxYear;
        }
        
        // 季度和月份选择器 - 确保选择的年份在可用范围内
        if (selectedPicker === FrequencyType.Quarterly || selectedPicker === FrequencyType.Monthly) {
          if (dayjsDate.year() < minYear || dayjsDate.year() > maxYear) {
            return true;
          }
        }
      }
    }

    // 如果提供了最小日期，则禁用早于最小日期的所有日期
    if (minDate && dayjsDate.isBefore(minDate, 'day')) {
      return true;
    }

    return false;
  };

  // 渲染选择器
  const renderPicker = () => {
    if (selectedPicker === FrequencyType.Weekly) {
      return (
        <div 
          className={`custom-weekly-input ${!calendarData ? 'disabled' : ''}`}
          ref={inputRef} 
          onClick={calendarData ? handleWeeklyCalendarToggle : undefined}
          role="button"
          tabIndex={disabled || !calendarData ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled && calendarData) {
              handleWeeklyCalendarToggle();
            }
          }}
          style={{ width: '100%', minWidth: 0, flex: 1 }}
        >
          <input 
            className="weekly-picker-input"
            readOnly
            placeholder={!calendarData ? "加载中..." : getPlaceholder(selectedPicker)}
            value={selectedDate ? formatDate(selectedDate) : ''}
            disabled={disabled || !calendarData}
          />
          {allowClear && selectedDate && calendarData ? (
            <span 
              className="clear-icon"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDate(null);
                onChange({ type: selectedPicker, date: null, sequence: undefined });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  setSelectedDate(null);
                  onChange({ type: selectedPicker, date: null, sequence: undefined });
                }
              }}
            >
              <CloseIcon />
            </span>
          ) : (
            <span className="calendar-icon">
              <img src="/assets/icons/common/calendar.png" alt="日历" className="calendar-png-icon" />
            </span>
          )}
          {showWeeklyCalendar && calendarData && typeof window !== 'undefined' && 
            ReactDOM.createPortal(
              <WeeklyCalendar 
                calendarData={calendarData}
                selectedDate={selectedDate}
                minDate={minDate}
                onChange={handleWeeklyCalendarChange}
                onClose={() => setShowWeeklyCalendar(false)}
              />,
              document.body
            )
          }
        </div>
      );
    }

    return (
      <DatePicker
        picker={mapFrequencyToPickerMode(selectedPicker)}
        value={selectedDate}
        onChange={handleDateChange}
        placeholder={!calendarData && selectedPicker !== undefined ? "加载中..." : getPlaceholder(selectedPicker)}
        disabled={selectedPicker === undefined || disabled || (selectedPicker !== FrequencyType.Yearly && selectedPicker !== FrequencyType.Quarterly && selectedPicker !== FrequencyType.Monthly && !calendarData)}
        format={(date) => formatDate(date)}
        allowClear={allowClear}
        disabledDate={disabledDate}
        style={{ width: '100%', minWidth: 0, flex: 1 }}
        popupStyle={{ zIndex: 1050 }}
        suffixIcon={<img src="/assets/icons/common/calendar.png" alt="日历" className="calendar-png-icon" />}
      />
    );
  };

  return (
    <div className="target-cycle-picker-container" style={{ width: '100%', display: 'flex', ...style }}>
      <Select
        placeholder="周期"
        value={selectedPicker}
        onChange={handleTypeChange}
        style={{ width: '80px', flexShrink: 0, marginRight: '4px' }}
        options={dateTypeOptions}
        allowClear={allowClear}
        disabled={disabled || disableTypeSelector}
        className="target-cycle-select"
      />
      <div 
        ref={datePickerRef}
        className={`date-picker-container ${selectedPicker === FrequencyType.Weekly ? 'weekly-picker' : ''}`}
        style={{ flex: 1, minWidth: 0, width: 'calc(100% - 85px)' }}
      >
        {renderPicker()}
      </div>
    </div>
  );
}
